import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { buyerSubmissionsApi } from "@/api/buyers";

import { listingMatchKeys } from "@/pages/buyers/presentBuyer/listingMatchKeys";

import { buyerKeys, buyerSubmissionKeys, listingKeys } from "./buyerKeys";

/**
 * Archive, restore and delete a submission — one at a time or in bulk (D1, D8,
 * D9, D12).
 *
 * <p>🔴 <b>Four prefixes, every time.</b> A submission's archive or delete
 * changes every Submissions table and its stats (`buyerSubmissionKeys.all`, which
 * also covers the present pickers' "Already presented" source), the buyer's own
 * record and its Associated listings badges (`buyerKeys.all`), and the listing's
 * record (`listingKeys.all`), and — since a delete frees the pair — the present
 * wizard's Suggested listings (`listingMatchKeys.all`). Invalidating one would
 * leave another surface telling a different story until it next refetched.</p>
 */
function invalidateSubmissionSurfaces(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
  void qc.invalidateQueries({ queryKey: buyerKeys.all });
  void qc.invalidateQueries({ queryKey: listingKeys.all });
  void qc.invalidateQueries({ queryKey: listingMatchKeys.all });
}

export function useArchiveSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buyerSubmissionsApi.archive(id),
    onSuccess: () => invalidateSubmissionSurfaces(qc),
  });
}

export function useRestoreSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buyerSubmissionsApi.restore(id),
    onSuccess: () => invalidateSubmissionSurfaces(qc),
  });
}

export function useDeleteSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => buyerSubmissionsApi.remove(id),
    onSuccess: () => invalidateSubmissionSurfaces(qc),
  });
}

export function useBulkArchiveSubmissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, archive }: { ids: string[]; archive: boolean }) =>
      archive ? buyerSubmissionsApi.bulkArchive(ids) : buyerSubmissionsApi.bulkRestore(ids),
    onSuccess: () => invalidateSubmissionSurfaces(qc),
  });
}

export function useBulkDeleteSubmissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => buyerSubmissionsApi.bulkDelete(ids),
    onSuccess: () => invalidateSubmissionSurfaces(qc),
  });
}
